import React, { useState } from 'react'
import { useLazyQuery } from '@apollo/client'
import { useParams } from 'react-router-dom'
import { Languages, X } from 'lucide-react'
import { Button as ButtonComponent } from '@/components/ui/button'
import { Input as InputComponent } from '@/components/ui/input'
import useAudienceTargetingStore from '@/store/audienceTargetingStore'
import { useMediaBuyingContext } from '@/context/media-buying-context'
import { AD_LOCALES, PUBLISHER_NAMES } from '@/graphql/account-signals-queries'
import { getHypothesisMap } from '@/utils/audience-builder'
import { ButtonProps } from '@/types'

// Type casts for the components from JSX files
const Button = ButtonComponent as React.FC<ButtonProps>
const Input = InputComponent as React.FC<React.InputHTMLAttributes<HTMLInputElement>>

export interface AdLocale {
    key: number
    name: string
}

export const LANGUAGE_EXPLANATION =
    'Optional. Leave empty to reach everyone in the location, whatever language they use Facebook and Instagram in. Add a language only when it is required, for example when your ads are in a language that is not the main language of the location, or the location has several languages.'

export const ADVANTAGE_LANGUAGE_NOTE =
    'With Advantage+ audience, languages stay a strict limit: Meta does not go beyond them.'

export const LANGUAGES_NOT_AVAILABLE = 'Languages are not available right now.'

export const MAX_LANGUAGES = 50

export const META_PUBLISHER_NAME = 'Meta'

interface PublisherName {
    id: string
    name: string
}

/** The caller's connection of the Meta publisher, or null when the licence is not connected to Meta. */
export const metaPublisherIdOf = (
    connectedPublishers: Record<string, unknown> | null | undefined,
    publishers: PublisherName[] | null | undefined,
): string | null => {
    const meta = (publishers || []).find((publisher) => publisher?.name === META_PUBLISHER_NAME)
    return meta && connectedPublishers?.[meta.id] ? meta.id : null
}

interface LanguageSettingsProps {
    stage: string
    audience: string
    getStepContentData: (step: string) => any
}

const LanguageSettings: React.FC<LanguageSettingsProps> = ({ stage, audience, getStepContentData }) => {
    const getAudienceSettings = useAudienceTargetingStore((state) => state.getAudienceSettings)
    const updateAudienceSetting = useAudienceTargetingStore((state) => state.updateAudienceSetting)
    // Subscribe to this audience's languages so the list re-renders on change
    useAudienceTargetingStore((state) => state.settings?.[stage]?.[audience]?.locales)
    const { id: campaignId } = useParams()
    const { connectedPublishers } = useMediaBuyingContext()
    const hasConnection = !!connectedPublishers && Object.keys(connectedPublishers).length > 0

    const [isSearching, setIsSearching] = useState(false)
    const [query, setQuery] = useState('')
    const [loadPublishers, publishersResult] = useLazyQuery(PUBLISHER_NAMES, { fetchPolicy: 'cache-first' })
    const [searchLocales, { data, loading, error }] = useLazyQuery(AD_LOCALES, { fetchPolicy: 'cache-first' })
    const publisherId = metaPublisherIdOf(connectedPublishers, publishersResult.data?.getPublishersFromPostgres)

    const stored = getAudienceSettings(stage, audience)?.locales
    const selected: AdLocale[] = Array.isArray(stored) ? stored : []
    const hypothesis = getHypothesisMap(getStepContentData('salesFunnelSetup'))[audience]
    const isAdvantage = hypothesis?.audienceMode === 'advantage'

    const canSearch = !!(publisherId && campaignId)
    const result = data?.adLocales
    const publishersPending = hasConnection && !!campaignId && !publishersResult.called
    const findingPublisher = publishersResult.loading || publishersPending
    const notAvailable = !findingPublisher && (!canSearch || !!error || result?.available === false)
    const options: AdLocale[] = (result?.locales || []).filter(
        (locale: AdLocale) => !selected.some((item) => item.key === locale.key),
    )

    const runSearch = (text: string) => {
        setQuery(text)
        if (canSearch) searchLocales({ variables: { publisherId, campaignId, query: text } })
    }

    const startSearch = async () => {
        setIsSearching(true)
        setQuery('')
        if (!hasConnection || !campaignId) return
        const response = await loadPublishers()
        const metaPublisherId = metaPublisherIdOf(connectedPublishers, response?.data?.getPublishersFromPostgres)
        if (metaPublisherId) {
            searchLocales({ variables: { publisherId: metaPublisherId, campaignId, query: '' } })
        }
    }

    const addLocale = (locale: AdLocale) => {
        if (selected.length >= MAX_LANGUAGES) return
        updateAudienceSetting(stage, audience, 'locales', [...selected, { key: locale.key, name: locale.name }])
    }

    const removeLocale = (key: number) => {
        updateAudienceSetting(
            stage,
            audience,
            'locales',
            selected.filter((item) => item.key !== key),
        )
    }

    return (
        <div
            className="tw:flex tw:gap-3 tw:flex-col tw:bg-secondary-background tw:rounded-xs tw:p-3"
            data-testid="language-settings"
        >
            <div className="tw:flex tw:gap-1.5 tw:items-center">
                <Languages className="tw:w-6 tw:h-6 tw:text-gray-200" />
                <span className="tw:font-semibold">Languages</span>
                <span className="tw:text-sm tw:text-gray-200">(optional)</span>
            </div>
            <p className="tw:text-sm tw:text-gray-200">{LANGUAGE_EXPLANATION}</p>
            {isAdvantage && (
                <p className="tw:text-sm tw:text-gray-200" data-testid="advantage-language-note">
                    {ADVANTAGE_LANGUAGE_NOTE}
                </p>
            )}

            {selected.length > 0 ? (
                <ul className="tw:flex tw:flex-wrap tw:gap-1.5" aria-label="Selected languages">
                    {selected.map((locale) => (
                        <li
                            key={locale.key}
                            className="tw:flex tw:items-center tw:gap-1 tw:rounded tw:bg-gray-400/40 tw:px-2 tw:py-0.5 tw:text-xs"
                        >
                            {locale.name}
                            <button
                                type="button"
                                aria-label={`Remove ${locale.name}`}
                                onClick={() => removeLocale(locale.key)}
                                className="tw:cursor-pointer"
                            >
                                <X className="tw:w-3 tw:h-3" />
                            </button>
                        </li>
                    ))}
                </ul>
            ) : (
                <span className="tw:text-sm" data-testid="no-language-limit">
                    No language limit
                </span>
            )}

            {!isSearching ? (
                <div>
                    <Button variant="link" className="tw:px-0" onClick={startSearch}>
                        Add a language
                    </Button>
                </div>
            ) : notAvailable ? (
                <span className="tw:text-sm tw:text-gray-200" data-testid="languages-not-available">
                    {LANGUAGES_NOT_AVAILABLE}
                </span>
            ) : (
                <div className="tw:flex tw:flex-col tw:gap-2">
                    <Input
                        aria-label="Search languages"
                        placeholder="Search languages"
                        value={query}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => runSearch(event.target.value)}
                    />
                    {loading || findingPublisher ? (
                        <span className="tw:text-sm tw:text-gray-200">Searching…</span>
                    ) : (
                        <ul className="tw:flex tw:flex-col tw:max-h-48 tw:overflow-y-auto" aria-label="Languages">
                            {options.map((locale) => (
                                <li key={locale.key}>
                                    <button
                                        type="button"
                                        className="tw:w-full tw:text-left tw:text-sm tw:px-2 tw:py-1 tw:hover:bg-slate-700 tw:cursor-pointer"
                                        onClick={() => addLocale(locale)}
                                        disabled={selected.length >= MAX_LANGUAGES}
                                    >
                                        {locale.name}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </div>
    )
}

export default LanguageSettings
